import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from './supabase';
import { API_ORIGIN } from './platform';
import { api, clearApiCache } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) loadProfile(session.user.id);
      else setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // A different (or no) user must never see the previous user's cached data
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') clearApiCache();
      setSession(session);
      if (session) loadProfile(session.user.id);
      else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function loadProfile(userId) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (!error && data) {
      setProfile(data);
      // Warm the main tabs so the first visit to each is instant
      [api.dashboard.chart, api.clinics.list, api.clinicians.list, api.settings.scoring, api.settings.clinics]
        .forEach((load) => load().catch(() => {}));
    } else {
      // Fallback: profile may not exist yet (new signup), use auth user metadata
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const meta = user.user_metadata || {};
        const fallbackProfile = {
          id: user.id,
          email: user.email,
          first_name: meta.first_name || '',
          last_name: meta.last_name || '',
        };
        // Try to create the profile
        await supabase.from('profiles').upsert(fallbackProfile);
        setProfile(fallbackProfile);
      }
    }
    setLoading(false);
  }

  async function signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  }

  async function signOut() {
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
  }

  async function resetPassword(email) {
    // Sent server-side via Resend so the email matches the app's branded template
    const res = await fetch(`${API_ORIGIN}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) throw new Error('Could not send reset email. Please try again.');
  }

  async function updatePassword(password) {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
  }

  return (
    <AuthContext.Provider value={{ session, profile, loading, signIn, signOut, resetPassword, updatePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
